// Lumeros brand marks — Brand Identity v3 (20 May 2026).
//
// Source of truth: `Brand identity/BRAND.md`. The brand is one wordmark and
// one full stop. There is NO icon, no lens, no monogram tile beyond `l.`.
//
// These render the official logo artwork shipped in `public/` (PNG exports of
// the Instrument Serif wordmark) rather than re-setting the type in CSS, so the
// product always shows pixel-identical brand assets. The correct light/dark
// cut is chosen from the active theme:
//   light theme (cream ground) → dark/ink wordmark
//   dark  theme (ink ground)   → light/cream wordmark
//
// Exports
//   <Wordmark size sx />        — full `lumeros.` wordmark
//   <ShortMark size sx />       — the `l.` short mark (favicon/avatar/rail)
//   <PeriodDot size accent />   — the period alone (loaders, status dots)
//   <BrandLockup size />        — wordmark for the AppBar / menu header

import React from 'react';
import { Box, useTheme } from '@mui/material';
import { SIGNAL } from '../theme';

// Long wordmark artwork — 341×91. Pick the cut that stays legible on the
// active ground.
const LONG = {
  light: '/Lumeros-long-dark.png',  // ink type — for the cream ground
  dark:  '/Lumeros-Long-light.png', // cream type — for the ink ground
};
// Short `l.` artwork — 49×121, transparent ground, green period.
const SHORT = {
  light: '/Lumeros-short-dark-no-bg.png',
  dark:  '/Lumeros-short-light-no-bg.png',
};
const LONG_RATIO  = 341 / 91;  // width ÷ height
const SHORT_RATIO = 49 / 121;

// ─────────────────────────────────────────────────────────────────────────────
// Wordmark — the full `lumeros.` lockup. `size` is the rendered height in px.
// ─────────────────────────────────────────────────────────────────────────────
export const Wordmark = ({ size = 24, sx }) => {
  const { palette } = useTheme();
  const src = LONG[palette.mode] || LONG.light;
  return (
    <Box
      component="img"
      src={src}
      alt="Lumeros"
      draggable={false}
      sx={{
        height: size,
        width: Math.round(size * LONG_RATIO),
        display: 'block',
        userSelect: 'none',
        ...sx,
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ShortMark — the `l.` form (BRAND.md §1, form 05). For favicons, avatars,
// and the collapsed menu rail. `size` is the rendered height in px.
// ─────────────────────────────────────────────────────────────────────────────
export const ShortMark = ({ size = 32, sx }) => {
  const { palette } = useTheme();
  const src = SHORT[palette.mode] || SHORT.light;
  return (
    <Box
      component="img"
      src={src}
      alt="Lumeros"
      draggable={false}
      sx={{
        height: size,
        width: Math.round(size * SHORT_RATIO),
        display: 'block',
        userSelect: 'none',
        ...sx,
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PeriodDot — the period alone in Signal green. Use for loaders, status
// dots, notification badges, watermarks. (BRAND.md §4)
// ─────────────────────────────────────────────────────────────────────────────
export const PeriodDot = ({ size = 10, accent = SIGNAL, sx }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: accent,
      flexShrink: 0,
      display: 'inline-block',
      ...sx,
    }}
  />
);

// ─────────────────────────────────────────────────────────────────────────────
// BrandLockup — the canonical AppBar / page-header lockup.
//
// Per BRAND.md §4: "The wordmark anchors the UI. Top-left of every product
// surface."
// ─────────────────────────────────────────────────────────────────────────────
export const BrandLockup = ({ size = 26, sx }) => <Wordmark size={size} sx={sx} />;

export default BrandLockup;
